import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Chip, CircularProgress } from '@mui/material';
import axios from 'axios';
import fileDownload from 'js-file-download';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { format } from 'date-fns';
import { Brand_Enum } from '@kheops/graphql';
import { currentContextAtom } from '../../state';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { CompanyInfo } from './CompanyInfoCard';

export interface ExportProductsButtonProps {
  supplierCompany: CompanyInfo;
}

function getFileNameByBrand(supplierCompany: CompanyInfo, brand?: Brand_Enum): string {
  const downloadDate = format(new Date(), 'yyyy-MM-dd-HH_mm');

  switch (brand) {
    case Brand_Enum.UExpress:
    case Brand_Enum.HyperU:
    case Brand_Enum.SuperU:
      return `${supplierCompany.tradeName}_Import_Matrice_Ulis_${downloadDate}.xlsx`;
    case Brand_Enum.CarrefourMarket:
      return `${supplierCompany.tradeName}_Import_Matrice_Caroline_Market_${downloadDate}.xlsx`;
    case Brand_Enum.CarrefourHyper:
      return `${supplierCompany.tradeName}_Import_Matrice_Caroline_Hyper_${downloadDate}.xlsx`;
    case Brand_Enum.IntermarcheHyper:
    case Brand_Enum.IntermarcheSuper:
    case Brand_Enum.IntermarcheExpress:
    case Brand_Enum.IntermarcheContact:
      return `${supplierCompany.tradeName}_Import_CADART_Intermarche_${downloadDate}.xlsx`;
    case Brand_Enum.Netto:
      return `${supplierCompany.tradeName}_Import_CADART_Netto_${downloadDate}.xlsx`;
    default:
      return `{{${supplierCompany.tradeName}}}(${supplierCompany.legalName})-${downloadDate}.xlsx`;
  }
}

export default function ExportProductsButton({ supplierCompany }: ExportProductsButtonProps): React.JSX.Element {
  const { t } = useTranslation(['error', 'common']);
  const { companyId, companyBrand } = useAtomValue(currentContextAtom);
  const [exportLoading, setExportLoading] = useState(false);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);

  const label = useMemo(() => {
    switch (companyBrand) {
      case Brand_Enum.Franprix:
        return 'products:export_product_ges_market';
      default:
        return 'common:products';
    }
  }, [companyBrand]);

  const handleExport = async (): Promise<void> => {
    setExportLoading(true);

    try {
      const fileName = getFileNameByBrand(supplierCompany, companyBrand || undefined);
      const res = await axios.get(`/export/company/${supplierCompany.id}/products?&buyerCompanyId=${companyId}`, { responseType: 'blob' });

      fileDownload(res.data, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch {
      setCommonSnackbarProps({
        label: t('error:an_error_occured'),
        snackbarProps: {
          open: true,
        },
        alertProps: {
          severity: 'error',
        },
      });
    }

    setExportLoading(false);
  };

  return (
    <Chip
      variant="assistive"
      icon={exportLoading ? <CircularProgress /> : <FileDownloadOutlinedIcon />}
      label={t(label)}
      onClick={handleExport}
      disabled={exportLoading}
      sx={{ width: 'fit-content' }}
    />
  );
}
